/**
 * 荷電粒子砲能力（LANCER専用機）
 *
 * このモジュールは荷電粒子砲のチャージ、発射、ダメージ判定、
 * および付随するエフェクトの生成ロジックを管理します。
 *
 * @module abilities/cannon
 */

import { CONFIG } from '../../config/game-config';
import {
    getCannonStock,
    setCannonStock,
    getCannonCharging,
    setCannonCharging,
    getCannonActive,
    setCannonActive,
    getCannonTimer,
    setScreenShake,
    getPlayer,
    getParticles,
    getPowerLevel,
    getCannonDamageTimer,
    setCannonDamageTimer,
    getEnemies,
    getEnemyBullets, setCannonTimer
} from '../../store/index';
import { getP5 } from '../../core/p5-instance';
import { handleEnemyDefeat } from '../enemy/enemy';
import { createHitParticles } from '../particles/particles';
import { updateCannonEffect } from './cannon-effects';

/**
 * 荷電粒子砲のチャージ開始処理
 * ストックを消費し、チャージ状態へ移行します。
 */
export function useCannon(): void {
    const p = getP5();
    const cannonStock = getCannonStock();
    const cannonCharging = getCannonCharging();
    const cannonActive = getCannonActive();

    // ストック不足、または既に使用中の場合は何もしない
    if (cannonStock <= 0 || cannonCharging || cannonActive) {
        return;
    }

    // 状態の更新
    setCannonStock(cannonStock - 1);
    setCannonCharging(true);
    setCannonTimer(CONFIG.ships.lancer.cannonChargeDuration);
    setScreenShake(3); // チャージ開始時の軽い揺れ

    const player = getPlayer();
    if (!player) {
        return;
    }

    const particles = getParticles();

    // チャージ開始時のパーティクル演出
    for (let i = 0; i < 10; i++) {
        const angle = (p.TWO_PI / 10) * i;
        particles.push({
            x: player.x,
            y: player.y - 20,
            vx: p.cos(angle) * 3,
            vy: p.sin(angle) * 3,
            size: p.random(3, 6),
            color: p.color(200, 50, 255),
            life: p.random(20, 40)
        });
    }
}

/**
 * 荷電粒子砲の状態更新ロジック
 * チャージの進行、発射タイマーの管理、当たり判定、敵弾消去を行います。
 *
 * @param dt 経過時間（フレーム倍率）
 */
export function updateCannon(dt: number): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    // 視覚演出の状態更新（揺れなど）
    updateCannonEffect();

    // --- チャージフェーズ ---
    if (getCannonCharging()) {
        let timer = getCannonTimer();
        timer -= dt;
        setCannonTimer(timer);

        if (timer <= 0) {
            // チャージ完了→発射フェーズへ移行
            setCannonCharging(false);
            setCannonActive(true);

            const powerLevel = getPowerLevel();
            const cannonCfg = CONFIG.ships.lancer.cannonByPower;
            const lvIndex = p.constrain(powerLevel - 1, 0, 3);

            // 発射持続時間をパワーレベルに応じて設定
            setCannonTimer(cannonCfg.duration[lvIndex]);
            setCannonDamageTimer(0); // 即座に初回のダメージ判定を行う
            setScreenShake(12 + powerLevel * 2); // 発射時の激しい揺れ

            // 発射瞬間のパーティクル演出
            const particles = getParticles();
            const particleCount = 20 + powerLevel * 5;
            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: player.x + p.random(-20 - powerLevel * 3, 20 + powerLevel * 3),
                    y: p.random(0, p.height),
                    vx: p.random(-2, 2),
                    vy: p.random(-5, -15),
                    size: p.random(3, 8),
                    color: p.color(200, 100, 255),
                    life: p.random(20, 40)
                });
            }
        }
    }

    // --- 発射フェーズ ---
    if (getCannonActive()) {
        let timer = getCannonTimer();
        let damageTimer = getCannonDamageTimer();

        timer -= dt;
        damageTimer -= dt;

        setCannonTimer(timer);
        setCannonDamageTimer(damageTimer);

        const powerLevel = getPowerLevel();
        const cannonCfg = CONFIG.ships.lancer.cannonByPower;
        const lvIndex = p.constrain(powerLevel - 1, 0, 3);

        // ビーム幅の計算（サイン波でわずかに脈動させる）
        const baseBeamWidth = cannonCfg.beamWidth[lvIndex];
        const cannonDamage = cannonCfg.damage[lvIndex];
        const beamWidth = baseBeamWidth + p.sin(p.frameCount * 0.5) * (8 + powerLevel * 2);

        const beamLeft = player.x - beamWidth;
        const beamRight = player.x + beamWidth;

        // ダメージ判定の間隔管理（ティック処理）
        if (damageTimer <= 0) {
            setCannonDamageTimer(CONFIG.ships.lancer.cannonDamageInterval);
            performCannonTick(beamLeft, beamRight, cannonDamage);
        }

        // 発射終了判定
        if (timer <= 0) {
            setCannonActive(false);
        }
    }
}

/**
 * 荷電粒子砲のダメージティック処理
 * ビーム範囲内の敵へのダメージ適用と敵弾の消去を行います。
 *
 * @param beamLeft ビームの左端座標
 * @param beamRight ビームの右端座標
 * @param damage 与えるダメージ量
 */
function performCannonTick(beamLeft: number, beamRight: number, damage: number): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const enemies = getEnemies();
    const enemyBullets = getEnemyBullets();
    const particles = getParticles();

    // ビーム範囲内の敵へのダメージ適用
    for (let i = enemies.length - 1; i >= 0; i--) {
        const enemy = enemies[i];
        // 左右の範囲チェックと、プレイヤーより前方にいるかのチェック
        if (enemy.x + enemy.size / 2 >= beamLeft &&
            enemy.x - enemy.size / 2 <= beamRight &&
            enemy.y < player.y - 30) {

            enemy.hp -= damage;
            createHitParticles(enemy.x, enemy.y);

            if (enemy.hp <= 0) {
                handleEnemyDefeat(enemy);
                enemies.splice(i, 1);
            }
        }
    }

    // ビーム範囲内の敵弾を一掃
    for (let i = enemyBullets.length - 1; i >= 0; i--) {
        const b = enemyBullets[i];
        if (b.x >= beamLeft && b.x <= beamRight && b.y < player.y - 30) {
            // 弾が消滅する際の火花演出
            particles.push({
                x: b.x,
                y: b.y,
                vx: p.random(-2, 2),
                vy: p.random(-2, 2),
                size: p.random(3, 6),
                color: p.color(255, 200, 255),
                life: p.random(10, 20)
            });
            enemyBullets.splice(i, 1);
        }
    }
}